import { useContext, useEffect, useId, useRef, useState } from 'react';

import {
  type Campaign,
  contentPath,
  declineIdea,
  duplicateContent,
  ideaBatchPath,
  reopenIdea,
  setContentArchived,
} from '../api.ts';
import { ConfirmDialog } from '../ConfirmDialog.tsx';
import { FUNNEL_LABEL } from '../BriefDetailsFields.tsx';
import { describeError } from '../data.tsx';
import { Icon } from '../Icon.tsx';
import { IdeaSection, isSection, seedLine } from '../IdeaSection.tsx';
import { Link } from '../router.tsx';
import { useToast } from '../toast.tsx';
import { Facts, IdeaStatusBadge, Markdown, Notice, StatusBadge } from '../ui.tsx';
import * as wording from '../wording.ts';
import { CardFields } from './CardFields.tsx';
import { ChecklistEditor } from './ChecklistEditor.tsx';
import { PlanningConversation } from './PlanningConversation.tsx';
import { PostsPanel } from './PostsPanel.tsx';
import { CardActionsContext, PrimaryActionButton } from './PrimaryActionButton.tsx';
import { hasStageSection, StageSection } from './StageSection.tsx';
import { hasVersionSection, VersionSection } from './VersionSection.tsx';
import { BOARD_COLUMNS, type BoardCard, cardHref, cardTitle, type ContentCard, type IdeaCard } from './model.ts';

/**
 * The card drawer (US-018): a panel on the right that leaves the board usable
 * beside it, about the one card named by `card=<cardId>` in the address. Its
 * header says what the card is and where it stands; below it the card's own
 * fields; for an idea what it says and Decline (US-020), for a content its
 * checklist, brief details, and Duplicate and Archive. Esc and the close button close it; the page puts the focus back on
 * the card.
 */
export function Drawer({
  cardId,
  card,
  campaigns,
  showsArchived,
  planningFocus,
  campaignFocus,
  onClose,
  onOpen,
  onChanged,
}: {
  readonly cardId: string;
  /** The card as the board last read it; `undefined` when it is not on the board. */
  readonly card: BoardCard | undefined;
  readonly campaigns: readonly Campaign[];
  /** Whether the board shows archived contents, i.e. whether an archived card stays. */
  readonly showsArchived: boolean;
  /**
   * Bumped by **Answer Claude** (US-023): scroll to the planning conversation
   * and focus its reply box instead of the close button.
   */
  readonly planningFocus?: number;
  /** Bumped by `e` (US-027): focus the campaign field instead of the close button. */
  readonly campaignFocus?: number;
  readonly onClose: () => void;
  /** Opens another card's drawer: the copy a duplicate made. */
  readonly onOpen: (cardId: string) => void;
  /** Something changed: the board should read again. */
  readonly onChanged: () => Promise<void>;
}) {
  const titleId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const campaignRef = useRef<HTMLSelectElement>(null);

  // The drawer takes the focus when it opens on a card, so Esc and Tab start
  // here; opened to answer Claude, the reply box takes it instead.
  const focusesReply = planningFocus !== undefined;
  const focusesCampaign = campaignFocus !== undefined;
  useEffect(() => {
    if (!focusesReply && !focusesCampaign) closeRef.current?.focus();
  }, [cardId, focusesReply, focusesCampaign]);
  useEffect(() => {
    if (campaignFocus !== undefined) campaignRef.current?.focus();
  }, [cardId, campaignFocus]);

  // Esc closes the drawer from anywhere on the page, a field inside it too,
  // unless a dialog is open: Esc is that dialog's.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (document.querySelector('dialog[open]') !== null) return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const column = card === undefined ? undefined : BOARD_COLUMNS.find((entry) => entry.id === card.column);

  return (
    <aside className="board-drawer" aria-labelledby={titleId}>
      <header className="board-drawer__header">
        <div className="board-drawer__heading">
          <h2 className="board-drawer__title" id={titleId}>
            {card === undefined ? (
              wording.BOARD_DRAWER
            ) : (
              <Link className="board-drawer__link" href={cardHref(card)}>
                {cardTitle(card)}
              </Link>
            )}
          </h2>
          {card !== undefined && (
            <p className="board-drawer__meta">
              {column !== undefined && <span>{column.label}</span>}
              {card.type === 'content' ? (
                <StatusBadge status={card.content.status} />
              ) : (
                <IdeaStatusBadge
                  status={card.idea.status}
                  {...(card.idea.error === null ? {} : { title: card.idea.error })}
                />
              )}
            </p>
          )}
        </div>
        <button
          ref={closeRef}
          type="button"
          className="button button--quiet button--small button--icon"
          title={wording.BOARD_DRAWER_CLOSE}
          aria-label={wording.BOARD_DRAWER_CLOSE}
          onClick={onClose}
        >
          <Icon name="x" />
        </button>
      </header>
      {/* In Review, Approve stands under the version it approves (US-022), not up here. */}
      {card !== undefined && !actsInSection(card) && (
        <PrimaryActionButton card={card} variant="drawer" />
      )}
      <div className="board-drawer__body">
        {card === undefined ? (
          <p className="field__hint">{wording.BOARD_DRAWER_MISSING}</p>
        ) : (
          <>
            <CardFields
              // A new card is a new form: nothing typed for one is saved on the other.
              key={card.cardId}
              cardId={card.cardId}
              campaignId={card.type === 'content' ? card.content.campaignId : card.idea.campaignId}
              dueOn={card.type === 'content' ? card.content.dueOn : card.idea.dueOn}
              blockedReason={card.type === 'content' ? card.content.blockedReason : card.idea.blockedReason}
              campaigns={campaigns}
              campaignRef={campaignRef}
              onSaved={() => {
                void onChanged();
              }}
            />
            {card.type === 'idea' && (
              <IdeaSections key={`idea-${card.cardId}`} card={card} onClose={onClose} onChanged={onChanged} />
            )}
            {card.type === 'content' && (
              <ContentSections
                key={`content-${card.cardId}`}
                card={card}
                showsArchived={showsArchived}
                {...(planningFocus === undefined ? {} : { planningFocus })}
                onClose={onClose}
                onOpen={onOpen}
                onChanged={onChanged}
              />
            )}
          </>
        )}
      </div>
    </aside>
  );
}

/**
 * Whether the card's primary action stands in a section of the drawer rather
 * than under its header: Approve under the version in Review (US-022), and
 * Mark ready, Answer Claude and Plan with Claude in the planning conversation
 * of a pending content (US-023). A content whose setup failed keeps **Fetch
 * the project files again** up here: there is nothing to plan in yet.
 */
function actsInSection(card: BoardCard): boolean {
  if (card.type !== 'content') return false;
  const { content } = card;
  return content.status === 'review' || (content.status === 'pending' && content.lastError === null);
}

/**
 * What only an idea's drawer has (US-020): the line it came from, its batch,
 * what Claude made of it (or why it could not), and **Decline** and **Edit in
 * review** beside the primary action (Approve, or Try again).
 */
function IdeaSections({
  card,
  onClose,
  onChanged,
}: {
  readonly card: IdeaCard;
  readonly onClose: () => void;
  readonly onChanged: () => Promise<void>;
}) {
  const toast = useToast();
  const reasonId = useId();
  const { idea, batch } = card;
  const [declining, setDeclining] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);
  const title = cardTitle(card);

  const decline = async (): Promise<void> => {
    const trimmed = reason.trim();
    setBusy(true);
    setRefusal(null);
    try {
      await declineIdea(batch.id, idea.id, trimmed === '' ? null : trimmed);
      setDeclining(false);
      // A declined idea leaves the board, and with it the drawer.
      onClose();
      toast.ok(wording.boardIdeaDeclined(title), {
        label: wording.UNDO,
        onClick: () => {
          reopenIdea(batch.id, idea.id)
            .then(onChanged)
            .catch((cause: unknown) => {
              toast.error(`${wording.UNDO_FAILED} ${describeError(cause)}`);
            });
        },
      });
      await onChanged();
    } catch (cause: unknown) {
      // The refusal (the idea was decided meanwhile) stays in the dialog.
      setRefusal(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  const text = (value: string | null) => (value === null || value === '' ? <span className="muted">—</span> : value);

  return (
    <>
      <section className="board-drawer__section">
        <h3 className="board-drawer__section-title">{wording.BOARD_YOUR_LINE}</h3>
        {isSection(idea) ? (
          <>
            <p>{seedLine(idea)}</p>
            <IdeaSection seed={idea.seed} />
          </>
        ) : (
          <p>{idea.seed}</p>
        )}
        <Facts
          items={[
            {
              label: wording.BOARD_IDEA_BATCH,
              value: (
                <Link className="link" href={ideaBatchPath(batch.id)}>
                  {batch.title}
                </Link>
              ),
            },
          ]}
        />
      </section>
      {idea.status === 'failed' && idea.error !== null && <Notice kind="error">{idea.error}</Notice>}
      {idea.status === 'drafted' && (
        <>
          <Facts
            items={[
              { label: wording.IDEA_KIND_LABEL, value: text(idea.kind === null ? null : wording.IDEA_KIND[idea.kind]) },
              {
                label: wording.BOARD_IDEA_LENGTH,
                value:
                  idea.targetSeconds === null ? (
                    <span className="muted">{wording.BOARD_IDEA_LENGTH_OPEN}</span>
                  ) : (
                    wording.length(idea.targetSeconds)
                  ),
              },
              { label: wording.IDEA_TONE, value: text(idea.tone) },
              {
                label: wording.IDEA_FORMATS,
                value: text(idea.formats === null || idea.formats.length === 0 ? null : idea.formats.join(', ')),
              },
            ]}
          />
          <section className="board-drawer__section">
            <h3 className="board-drawer__section-title">{wording.IDEA_BRIEF}</h3>
            <Markdown text={idea.brief ?? ''} />
          </section>
          {idea.creativeDirection !== null && idea.creativeDirection !== '' && (
            <section className="board-drawer__section">
              <h3 className="board-drawer__section-title">{wording.IDEA_DIRECTION}</h3>
              <p>{idea.creativeDirection}</p>
            </section>
          )}
          {idea.rationale !== null && idea.rationale !== '' && (
            <section className="board-drawer__section">
              <h3 className="board-drawer__section-title">{wording.IDEA_RATIONALE}</h3>
              <p className="muted">{idea.rationale}</p>
            </section>
          )}
        </>
      )}
      <div className="board-drawer__actions">
        {idea.status === 'drafted' && (
          <button
            type="button"
            className="button"
            onClick={() => {
              setRefusal(null);
              setDeclining(true);
            }}
          >
            <Icon name="x" />
            {wording.DECLINE}
          </button>
        )}
        <Link className="button" href={cardHref(card)}>
          {wording.BOARD_EDIT_IN_REVIEW}
        </Link>
      </div>
      <ConfirmDialog
        open={declining}
        title={wording.DECLINE_TITLE}
        confirmLabel={wording.DECLINE_YES}
        cancelLabel={wording.CANCEL}
        busy={busy}
        onConfirm={() => void decline()}
        onCancel={() => {
          setDeclining(false);
          setReason('');
          setRefusal(null);
        }}
      >
        <p>
          <strong>{title}</strong>
        </p>
        <label className="field__label" htmlFor={reasonId}>
          {wording.DECLINE_REASON}
        </label>
        <textarea
          id={reasonId}
          className="field__input"
          rows={3}
          maxLength={500}
          value={reason}
          onChange={(event) => {
            setReason(event.target.value);
          }}
        />
        <p className="field__hint">{wording.DECLINE_REASON_HINT}</p>
        {refusal !== null && (
          <p className="field__hint field__hint--error" role="alert">
            {refusal}
          </p>
        )}
      </ConfirmDialog>
    </>
  );
}

/**
 * What only a content's drawer has: where it stands in planning and production
 * (US-021), the version in review and after (US-022), its posts (US-024), checklist, brief details, Duplicate and Archive.
 */
function ContentSections({
  card,
  showsArchived,
  planningFocus,
  onClose,
  onOpen,
  onChanged,
}: {
  readonly card: ContentCard;
  readonly showsArchived: boolean;
  readonly planningFocus?: number;
  readonly onClose: () => void;
  readonly onOpen: (cardId: string) => void;
  readonly onChanged: () => Promise<void>;
}) {
  const toast = useToast();
  const actions = useContext(CardActionsContext);
  const content = card.content;
  const details = content.briefDetails;
  const [archiving, setArchiving] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const archived = content.archivedAt !== null;

  const archive = async (): Promise<void> => {
    setArchiving(true);
    try {
      await setContentArchived(content.id, !archived);
      toast.ok(archived ? wording.boardUnarchived(content.name) : wording.boardArchived(content.name));
      // Off the board unless Archived is ticked: the drawer would have nothing to show.
      if (!archived && !showsArchived) onClose();
      await onChanged();
    } catch (cause: unknown) {
      // 409 `content_running` says why in its message.
      toast.error(describeError(cause));
    } finally {
      setArchiving(false);
    }
  };

  const text = (value: string | null) => value ?? <span className="muted">—</span>;

  return (
    <>
      {hasStageSection(card) && <StageSection card={card} onChanged={onChanged} />}
      {content.status === 'pending' && (
        <PlanningConversation
          contentId={content.id}
          canStart={content.containerId !== null && content.lastError === null}
          whenIdle="offer"
          {...(planningFocus === undefined ? {} : { focusReply: planningFocus })}
          onMarkReady={() => {
            actions?.openDialog('mark-ready', card);
          }}
          onChanged={() => {
            void onChanged();
          }}
        />
      )}
      {hasVersionSection(card) && <VersionSection card={card} />}
      <PostsPanel contentId={content.id} posts={card.posts} content={content} onChanged={onChanged} />
      <ChecklistEditor
        contentId={content.id}
        items={content.checklist}
        onSaved={() => {
          void onChanged();
        }}
      />
      <section className="board-drawer__section">
        <h3 className="board-drawer__section-title">{wording.BOARD_BRIEF_DETAILS}</h3>
        <Facts
          items={[
            { label: 'Goal', value: text(details.goal) },
            { label: 'Audience', value: text(details.audience) },
            {
              label: 'Funnel',
              value: text(
                details.funnelStages.length === 0
                  ? null
                  : details.funnelStages.map((stage) => FUNNEL_LABEL[stage]).join(', '),
              ),
            },
            { label: 'Angle', value: text(details.angle) },
            { label: 'Placement', value: text(details.placement) },
          ]}
        />
        <Link className="link" href={contentPath(content.id)}>
          {wording.BOARD_EDIT_ON_CONTENT_PAGE}
        </Link>
      </section>
      <div className="board-drawer__actions">
        <button
          type="button"
          className="button"
          onClick={() => {
            setDuplicating(true);
          }}
        >
          <Icon name="duplicate" />
          {wording.BOARD_DUPLICATE}
        </button>
        <button type="button" className="button" disabled={archiving} onClick={() => void archive()}>
          <Icon name="archive" />
          {archived ? wording.BOARD_UNARCHIVE : wording.BOARD_ARCHIVE}
        </button>
      </div>
      <DuplicateDialog
        open={duplicating}
        contentId={content.id}
        sourceName={content.name}
        onCancel={() => {
          setDuplicating(false);
        }}
        onDuplicated={(id) => {
          setDuplicating(false);
          onOpen(`content:${id}`);
          void onChanged();
        }}
      />
    </>
  );
}

/**
 * Asks the copy's name (`<name>-copy` unless changed) and makes the copy
 * through `POST /api/contents/:id/duplicate`. Standalone, so the content
 * page's header (US-031) can open the same dialog. A refusal (a taken name,
 * a campaign since archived) stays in the dialog, which stays open.
 */
export function DuplicateDialog({
  open,
  contentId,
  sourceName,
  onCancel,
  onDuplicated,
}: {
  readonly open: boolean;
  readonly contentId: string;
  readonly sourceName: string;
  readonly onCancel: () => void;
  /** The copy exists; its id. */
  readonly onDuplicated: (id: string) => void;
}) {
  const toast = useToast();
  const inputId = useId();
  const suggested = `${sourceName}-copy`;
  const [name, setName] = useState(suggested);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Each opening starts from the suggestion.
  useEffect(() => {
    if (!open) return;
    setName(suggested);
    setError(null);
  }, [open, suggested]);

  const confirm = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const trimmed = name.trim();
      // The untouched suggestion is the server's to make unique (`-copy-2`, …).
      const { content } = await duplicateContent(contentId, trimmed === suggested ? undefined : trimmed);
      toast.ok(wording.boardDuplicated(content.name), { href: contentPath(content.id) });
      onDuplicated(content.id);
    } catch (cause: unknown) {
      setError(describeError(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ConfirmDialog
      open={open}
      title={wording.BOARD_DUPLICATE_TITLE}
      confirmLabel={wording.BOARD_DUPLICATE}
      busyLabel={wording.BOARD_DUPLICATING}
      busy={busy}
      confirmDisabled={name.trim() === ''}
      onConfirm={() => void confirm()}
      onCancel={onCancel}
    >
      <p>{wording.BOARD_DUPLICATE_BODY}</p>
      <div className="field">
        <label className="field__label" htmlFor={inputId}>
          {wording.BOARD_DUPLICATE_NAME}
        </label>
        <input
          id={inputId}
          className="field__input"
          type="text"
          maxLength={60}
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && name.trim() !== '' && !busy) {
              event.preventDefault();
              void confirm();
            }
          }}
        />
        {error !== null && (
          <p className="field__hint field__hint--error" role="alert">
            {error}
          </p>
        )}
      </div>
    </ConfirmDialog>
  );
}
